import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'

const AuthContext = createContext({})

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchProfile(session.user.id)
      else setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchProfile(session.user.id)
      else {
        setProfile(null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const fetchProfile = async (userId) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()

    // Fallback: se il profilo non ha dati creativi/cliente ma i metadata li hanno,
    // li applica ora (copertura per quando il trigger ha girato senza sessione attiva)
    if (data) {
      const { data: { user } } = await supabase.auth.getUser()
      const meta = user?.user_metadata || {}
      const missingCreative = !data.ruolo_creativo && !data.bio && !data.email_contatto && !data.linkedin_url && !data.portfolio_url
      const missingRole = !data.role || data.role === 'member' // 'member' è il default, potrebbe essere 'client'
      const hasMetaData = meta.role || meta.ruolo_creativo || meta.bio || meta.azienda

      if (hasMetaData && (missingCreative || (missingRole && meta.role === 'client'))) {
        const update = {}
        if (meta.role) update.role = meta.role
        if (meta.ruolo_creativo) update.ruolo_creativo = meta.ruolo_creativo
        if (meta.bio) update.bio = meta.bio
        if (meta.email_contatto) update.email_contatto = meta.email_contatto
        if (meta.linkedin_url) update.linkedin_url = meta.linkedin_url
        if (meta.website_url) update.website_url = meta.website_url
        if (meta.portfolio_url) update.portfolio_url = meta.portfolio_url
        if (meta.disponibile !== undefined) update.disponibile = meta.disponibile === 'true' || meta.disponibile === true
        if (meta.azienda) update.azienda = meta.azienda

        const { data: updated } = await supabase
          .from('profiles').update(update).eq('id', userId).select('*').single()
        if (updated) {
          setProfile(updated)
          setLoading(false)
          return
        }
      }
    }

    setProfile(data)
    setLoading(false)
  }

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id)
  }

  const signIn = (email, password) =>
    supabase.auth.signInWithPassword({ email, password })

  const signOut = () => supabase.auth.signOut()

  const getDisplayName = (p) => {
    const prof = p || profile
    if (!prof) return 'Utente'
    if (prof.display_name_preference === 'full_name') return prof.full_name || prof.nickname || 'Utente'
    return prof.nickname || prof.full_name || 'Utente'
  }

  return (
    <AuthContext.Provider value={{ user, profile, loading, signIn, signOut, refreshProfile, getDisplayName }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)